import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";
import { useFormField } from "../../lib/form-field-context.js";
import { CHOICE_DETAIL, type ChoiceVariant } from "../../lib/choice-card.js";
import type { CategoricalTint } from "../../lib/tints.js";
import { RadioOptionRow } from "./RadioOptionRow.js";

/* ---------------- RadioGroup ----------------
   Curated API (option list + string value/onChange) that forwards a ref to and
   spreads rest props onto the group root. The value contract stays
   component-owned: `value`/`defaultValue`/`onChange` are Omitted from the
   spread types so a native `<div value=…>`/event `onChange` can't shadow them. */

export interface RadioOption {
  value: string;
  label: React.ReactNode;
  /** Secondary text under the label. Linked with aria-describedby; never
   *  part of the accessible name. */
  description?: React.ReactNode;
  /** A decorative glyph before the label (aria-hidden). */
  icon?: React.ReactNode;
  /** Content at the row's end edge, OUTSIDE the label, so a button here is
   *  never nested inside the option. */
  trailing?: React.ReactNode;
  /** Why the option cannot be chosen. Its presence disables the option and
   *  shows the reason as visible text linked with aria-describedby. There is
   *  no separate disabled flag: an option cannot be disabled without one. */
  disabledReason?: string;
  /** The swatch colour (`variant="swatch"` only), from lib/tints.ts. No raw
   *  colour value typechecks. */
  tint?: CategoricalTint;
}

/** `card` is a bordered, full-width row at least 44px tall; the checked row
 *  takes the selected paint Card uses. `swatch` renders each option as a
 *  24px tinted circle in a wrapping row — colour choices, not text options
 *  (RadioOption.tint). */
export type RadioGroupVariant = ChoiceVariant | "swatch";

export interface RadioGroupProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onChange" | "defaultValue" | "value"
> {
  name?: string;
  value?: string;
  defaultValue?: string;
  options: RadioOption[];
  onChange?: (value: string) => void;
  /** Visual style of every option row. Default `default`. */
  variant?: RadioGroupVariant;
}

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  function RadioGroup(
    {
      name,
      value,
      defaultValue,
      options,
      onChange,
      variant = "default",
      className,
      id,
      "aria-labelledby": ariaLabelledby,
      "aria-describedby": ariaDescribedby,
      "aria-invalid": ariaInvalid,
      "aria-required": ariaRequired,
      ...rest
    },
    ref,
  ) {
    const autoName = React.useId();
    const groupName = name ?? autoName;
    const isControlled = value !== undefined;
    // The uncontrolled default skips an option that cannot be chosen.
    const [internal, setInternal] = React.useState(
      defaultValue ??
        options.find((o) => o.disabledReason === undefined)?.value,
    );
    const current = isControlled ? value : internal;
    const field = useFormField();
    // A swatch is a 24px circle with no room for text inside it: unlike the
    // default/card variants (whose reason sits inside the option's own
    // label), every disabledReason among swatch options renders as visible
    // text below the whole row — the same below-control convention
    // SegmentedControl uses for its own icon-row options — so the group
    // wrapper below exists only when there is a reason to show.
    const hasSwatchReasons =
      variant === "swatch" &&
      options.some((o) => o.disabledReason !== undefined);
    const group = (
      <div
        {...rest}
        ref={ref}
        className={cn(
          variant === "swatch"
            ? "flex flex-row flex-wrap gap-2"
            : "flex flex-col gap-2",
          !hasSwatchReasons && className,
        )}
        role="radiogroup"
        id={field?.id ?? id}
        aria-labelledby={field?.labelId ?? ariaLabelledby}
        aria-describedby={field?.describedBy ?? ariaDescribedby}
        aria-invalid={field?.invalid ?? ariaInvalid}
        aria-required={field?.required ?? ariaRequired}
      >
        {options.map((o, index) => (
          <RadioOptionRow
            key={o.value}
            option={o}
            id={`${autoName}-${index}`}
            name={groupName}
            checked={current === o.value}
            variant={variant}
            onSelect={() => {
              if (!isControlled) setInternal(o.value);
              onChange?.(o.value);
            }}
          />
        ))}
      </div>
    );
    if (!hasSwatchReasons) return group;
    return (
      <div className={cn("flex flex-col items-start gap-1", className)}>
        {group}
        {options.map((o, index) =>
          o.disabledReason === undefined ? null : (
            <p
              key={o.value}
              id={`${autoName}-${index}-reason`}
              className={CHOICE_DETAIL}
            >
              {o.disabledReason}
            </p>
          ),
        )}
      </div>
    );
  },
);
